import { type ReactNode } from "react";

import { PrimRadioButtons } from "@/shared-web.ui/prim.radio-buttons";
import { Flex } from "@/shared-web.ui/ui.flex";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import {
  type ProductVariantOption,
} from "@/stf.data/schema";

import { useVariantState } from "./product-variant.states";

type VariantOptionWithDisabled = {
  disabled: boolean;
} & Pick<ProductVariantOption, "meta" | "optionKey" | "optionName">;

interface VariantButtonProps extends VariantOptionWithDisabled {}
const VariantButton = (props: VariantButtonProps) => {
  const {
    optionKey,
    optionName,
    disabled,
    meta,
  } = props;

  let children: ReactNode | string = optionName;
  if (meta.color) {
    children = (
      <>
        <div
          className="size-5 rounded-2 shadow-2"
          style={{ backgroundColor: meta.color.hexCode }}
        />
        {optionName}
      </>
    );
  }

  return (
    <PrimRadioButtons.Item
      value={optionKey}
      disabled={disabled}
      className="flex flex-row items-center gap-2"
    >
      {children}
    </PrimRadioButtons.Item>
  );
};

interface VariantSelectorProps {
  disabled?: boolean;
  idx: number;
  variantKey: string;
  variantName: string;
  variantOptions: VariantOptionWithDisabled[];
}
const VariantSelector = (props: VariantSelectorProps) => {
  const {
    idx,
    variantKey,
    variantName: label,
    variantOptions: options,
    disabled,
  } = props;

  const {
    getVal,
    opts,
    setVal,
  } = useVariantState();

  const val = getVal(variantKey);

  const onChange = (value: string) => {
    setVal(variantKey, value);
  };

  return (
    <Flex direction="column" gap="4">
      <Text as="p" trim="both">
        <Text
          as="span"
          mr="2"
          weight="medium"
          className={cn(
            disabled && "text-gray-9",
          )}
        >
          {label}
        </Text>
        {(opts && opts[idx])
          ? options.find((option) => {
            return option.optionKey === (opts[idx]);
          })?.optionName
          : null}
      </Text>
      <PrimRadioButtons.Root
        onValueChange={onChange}
        value={val || undefined}
      >
        {options.map((option) => {
          return (
            <VariantButton
              key={option.optionKey}
              {...option}
            />
          );
        })}
      </PrimRadioButtons.Root>
    </Flex>
  );
};

export { VariantSelector };
